import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertDialog,
  Body,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuItem,
  I,
  PageHeader,
  PageHeaderSkeleton,
  Pill,
  Row,
  SearchInput,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { Tooltip } from '@leanin/ui/client';
import { InviteByEmailModal } from '../../invites';
import { useMessageMember } from '../../groups/use-message-member';
import type { GroupMemberView } from '../../../lib/views/groups';
import { useInfiniteGroupMembers } from '../../../lib/queries/groups';
import { useSpace } from '../../../lib/queries/spaces';
import {
  useLeaveGroup,
  useRemoveGroupMember,
  useSetGroupMemberRole,
} from '../../../lib/mutations/groups';
import { useInviteToSpace } from '../../../lib/mutations/spaces';
import { useGroupLeaveFlow } from '../../group/use-group-leave-flow';
import { formatGroupMemberError } from '../../groups/format-group-member-error';
import { useConfirmDialog } from '../../../lib/use-confirm-dialog';
import { failureFromError } from '../../../lib/api-error';
import {
  MembersTableSkeleton,
  buildMemberColumns,
  filterMembersBySearch,
  memberName,
} from '../../group/admin/members-table';

type Props = {
  readonly id: string;
  readonly viewerUserId: string;
};

export function SpaceMembersSection({ id, viewerUserId }: Props) {
  const detailQuery = useSpace(id);
  const membersQuery = useInfiniteGroupMembers(id, { pageSize: 100 });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = membersQuery;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const navigate = useNavigate();
  const setRoleMutation = useSetGroupMemberRole();
  const removeMutation = useRemoveGroupMember();
  const leaveMutation = useLeaveGroup();
  const inviteMutation = useInviteToSpace();
  const { messageMember, error: messageError } = useMessageMember();

  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const remove = useConfirmDialog<GroupMemberView>();
  const demote = useConfirmDialog<GroupMemberView>();

  const leaveFlow = useGroupLeaveFlow({
    leave: (handlers) =>
      leaveMutation.mutate(
        { id, kind: 'space' },
        {
          onSuccess: () => {
            navigate({ to: '/groups' });
            handlers.onSuccess();
          },
          onError: handlers.onError,
        },
      ),
    fallbackMessage: 'Could not leave the Group.',
  });

  const isPending = setRoleMutation.isPending || removeMutation.isPending;

  const handlePromote = (m: GroupMemberView) => {
    setError(null);
    setRoleMutation.mutate(
      { groupId: id, userId: m.userId, role: 'leader' },
      {
        onError: (err) =>
          setError(formatGroupMemberError(err, 'Failed to update member role.')),
      },
    );
  };

  const confirmDemote = () => {
    if (demote.target === null) return;
    const target = demote.target;
    setError(null);
    setRoleMutation.mutate(
      { groupId: id, userId: target.userId, role: 'member' },
      {
        onSettled: () => demote.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, 'Failed to update member role.')),
      },
    );
  };

  const confirmRemove = () => {
    if (remove.target === null) return;
    const target = remove.target;
    setError(null);
    removeMutation.mutate(
      { groupId: id, userId: target.userId },
      {
        onSettled: () => remove.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, 'Failed to remove member.')),
      },
    );
  };

  const showSkeleton = useDelayedLoading(membersQuery.data === undefined);
  if (showSkeleton) {
    return (
      <Stack gap="lg">
        <PageHeaderSkeleton withAction />
        <MembersTableSkeleton rows={6} />
      </Stack>
    );
  }
  if (membersQuery.data === undefined) return null;

  const allMembers = membersQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const members = filterMembersBySearch(allMembers, search);
  const space = detailQuery.data ?? null;
  const spaceName = space?.name ?? 'this Group';
  const isClosed = space?.access === 'private';

  const viewerMembership = allMembers.find((m) => m.userId === viewerUserId);
  const viewerIsOnRoster = viewerMembership !== undefined;
  const leaderCount = allMembers.filter((m) => m.role === 'leader').length;
  const viewerIsOnlyLeader =
    viewerMembership?.role === 'leader' && leaderCount <= 1;

  const columns = buildMemberColumns<GroupMemberView>({
    viewerUserId,
    renderRole: (m) => (
      <Pill tone={m.role === 'leader' ? 'burgundy' : 'neutral'}>
        {m.role === 'leader' ? 'Leader' : 'Member'}
      </Pill>
    ),
    renderActions: (m, { name }) => {
      const isLeader = m.role === 'leader';
      return (
        <Row gap="xs" align="center" justify="flex-end">
          <Button
            variant="ghost"
            size="sm"
            icon={I.chat}
            aria-label={`Message ${name}`}
            onClick={() => messageMember(m.userId)}
          />
          <DropdownMenu
            align="end"
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={I.more}
                aria-label={`Manage ${name}`}
              />
            }
          >
            {!isLeader ? (
              <DropdownMenuItem
                onSelect={() => handlePromote(m)}
                disabled={isPending}
              >
                Promote to leader
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={() => demote.request(m)}
                disabled={isPending}
              >
                Demote to member
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              destructive
              disabled={isPending}
              onSelect={() => remove.request(m)}
            >
              Remove from Group
            </DropdownMenuItem>
          </DropdownMenu>
        </Row>
      );
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title="Members"
        subtitle="Search, change roles, and remove members."
        actions={
          <Row gap="sm" align="center">
            {!viewerIsOnRoster ? null : viewerIsOnlyLeader ? (
              <Tooltip content="You're the only leader. Promote someone to leader first.">
                <span tabIndex={0}>
                  <Button variant="secondary" size="md" disabled>
                    Leave Group
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="secondary"
                size="md"
                onClick={leaveFlow.openConfirm}
              >
                Leave Group
              </Button>
            )}
            {isClosed ? (
              <Button
                variant="primary"
                size="md"
                icon={I.mail}
                onClick={() => setInviteOpen(true)}
              >
                Invite by email
              </Button>
            ) : null}
          </Row>
        }
      />

      <Stack gap="md">
        <SearchInput
          placeholder="Search members…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <DataTable
          key={search}
          data={[...members]}
          columns={columns}
          pagination={{ pageSize: 10 }}
        >
          <DataTable.Header />
          <DataTable.Body emptyMessage="No members match." />
          <DataTable.Pagination />
        </DataTable>
        <Body size="sm" color="mute">
          {members.length} of {detailQuery.data?.memberCount ?? allMembers.length} members
        </Body>
      </Stack>

      {(error ?? messageError) !== null ? (
        <span role="alert" aria-live="polite">
          <Body size="sm" color="destructive">{error ?? messageError}</Body>
        </span>
      ) : null}

      {isClosed && space !== null ? (
        <InviteByEmailModal
          kind="email"
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          title={`Invite to ${spaceName}`}
          description="Send an invite by email. They'll get a link they can use to accept."
          onInvite={async (email) => {
            try {
              await inviteMutation.mutateAsync({ id: space.id, emails: [email] });
              return { ok: true };
            } catch (err) {
              return { ok: false, error: failureFromError(err, 'Invite failed.').message };
            }
          }}
        />
      ) : null}

      <AlertDialog
        open={remove.open}
        onOpenChange={remove.onOpenChange}
        title={
          remove.target !== null
            ? `Remove ${memberName(remove.target)} from this Group?`
            : 'Remove member?'
        }
        description={
          remove.target !== null
            ? `${memberName(remove.target)} will lose access to this Group's feed and members immediately. You can re-invite them later.`
            : ''
        }
        actionLabel="Remove member"
        cancelLabel="Keep member"
        destructive
        onAction={confirmRemove}
      />

      <AlertDialog
        open={demote.open}
        onOpenChange={demote.onOpenChange}
        title={
          demote.target !== null
            ? `Demote ${memberName(demote.target)} to member?`
            : 'Demote to member?'
        }
        description="They'll lose moderation access. You can promote them again from their member row."
        actionLabel="Demote"
        cancelLabel="Cancel"
        destructive
        onAction={confirmDemote}
      />

      <AlertDialog
        open={leaveFlow.confirmOpen}
        onOpenChange={leaveFlow.setConfirmOpen}
        title="Leave this Group?"
        description="You'll lose access to the Group's feed and members. You can rejoin later if the Group is open."
        actionLabel="Leave Group"
        cancelLabel="Stay"
        destructive
        onAction={leaveFlow.confirmLeave}
      />
      {leaveFlow.error !== null ? (
        <Body size="sm" color="destructive">{leaveFlow.error}</Body>
      ) : null}

    </Stack>
  );
}
